"use client";

import { useState } from "react";
import { Button, Link, Text } from "@stone-payments/jade";
import {
  setCookie,
  type CookieConsentDataState,
} from "@/services/cookie-consent";

type CookieConsentModalProps = {
  cookie?: CookieConsentDataState | null;
  deviceId: string;
};

const CookiesIcon = () => (
  <span className="jade-icon is-xlarge is-color-medium">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M12.2383 2.00684C13.0169 2.0322 13.6624 2.61787 13.7637 3.38965C13.808 3.69643 13.9904 4.11971 14.2871 4.47754C14.5461 4.78964 14.8639 5.01867 15.209 5.09863L15.3584 5.12402L15.3662 5.125L15.5566 5.15723C16.4906 5.36384 17.1638 6.20705 17.1367 7.18164C17.1121 8.10528 17.5336 8.89332 18.1914 9.47559C18.9972 10.1887 19.9312 10.3575 20.9424 10.5508C21.5676 10.743 22.0156 11.325 22.0117 12.0059L22.0078 12.2656C21.9352 14.9423 20.7937 17.4826 18.835 19.3174C16.9882 21.0532 14.5468 22.0142 12.0127 22.0039L12.0107 22.0049C11.7856 22.0049 11.5365 22.0054 11.2852 21.9805L10.9512 21.9473C8.68309 21.6991 6.56306 20.686 4.94531 19.0674C3.24643 17.3659 2.21502 15.1117 2.03809 12.7148C1.83382 9.93987 2.80694 7.20588 4.7168 5.18164C6.60693 3.16392 9.24683 2.01514 12.0117 2.00684H12.2383ZM11.9189 3.9209C9.71298 3.95089 7.61122 4.87497 6.10059 6.4873C4.1807 8.55165 3.47438 11.4687 4.23633 14.1836C4.99848 16.8973 7.12134 19.0199 9.83496 19.7822C12.55 20.5443 15.4669 19.8378 17.5312 17.918C19.0768 16.4694 19.9901 14.4774 20.0889 12.3711C17.3388 11.9994 15.1527 9.77575 15.2275 7.14746C15.2282 7.09191 15.1879 7.0436 15.1328 7.03516C14.2398 6.9297 13.4767 6.4426 12.918 5.82031C12.4215 5.26719 12.067 4.58736 11.9189 3.9209ZM12.6201 16.0039C12.6201 14.4384 14.9678 14.4384 14.9678 16.0039C14.9676 17.5692 12.6203 17.5692 12.6201 16.0039ZM8.6748 12.7969C9.59743 12.7971 10.3447 13.5451 10.3447 14.4678C10.3446 15.3915 9.59738 16.1394 8.6748 16.1396C7.75088 16.1396 7.00302 15.3917 7.00293 14.4678C7.00293 13.5449 7.75083 12.7969 8.6748 12.7969ZM11.1143 7.72461C12.1661 7.72482 13.0185 8.57782 13.0186 9.63086C13.0186 10.6828 12.1661 11.5359 11.1143 11.5361C10.061 11.5361 9.20801 10.6829 9.20801 9.63086C9.20804 8.57766 10.0611 7.72461 11.1143 7.72461Z"
        fill="currentColor"
      />
    </svg>
  </span>
);

export const CookieConsentModal = ({
  cookie,
  deviceId,
}: CookieConsentModalProps) => {
  const [open, setOpen] = useState(!cookie || cookie.consentRequired);

  const handleConsent = async (
    categories: CookieConsentDataState["categories"]
  ) => {
    setOpen(false);

    await setCookie({
      deviceId,
      categories,
      consentDate: new Date().getTime(),
    });
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed bottom-3 left-3 z-50 w-[calc(100%-1.5rem)] max-w-90 rounded-jade-medium border border-jade-border-medium bg-jade-background-surface shadow-lg md:bottom-4 md:left-4"
      role="dialog"
      aria-modal="true"
      aria-describedby="cookie-consent-description"
    >
      <div className="mb-4 flex flex-row gap-3 p-6 pb-0 md:flex-col">
        <div className="order-2 leading-0 md:order-1" aria-hidden="true">
          <CookiesIcon />
        </div>

        <div className="order-1 md:order-2">
          <Text
            variant="text-medium"
            weight="medium"
            id="cookie-consent-description"
          >
            Este site usa cookies para monitorar o desempenho e melhorar a
            experiência de uso.
          </Text>

          <Link
            href="https://docs.stone.com.br/portal-de-privacidade/"
            target="_blank"
            rel="noopener noreferrer"
            trailing="external"
          >
            Portal de Privacidade
          </Link>
        </div>
      </div>

      <div
        className="flex gap-2 p-4 pt-0"
        role="group"
        aria-label="Opções de consentimento"
      >
        <Button
          className="flex-1 md:flex-none"
          size="small"
          variant="neutral-subtle"
          onClick={() => handleConsent([])}
        >
          Rejeitar
        </Button>

        <Button
          className="flex-1 md:flex-none"
          size="small"
          variant="neutral-subtle"
          onClick={() => handleConsent(["analytics"])}
        >
          Aceitar
        </Button>
      </div>
    </div>
  );
};
